"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useMemo } from "react";

/**
 * Key Numbers 1026 — BDU Stats.
 *
 * Port of BDU v1 `components/Home/Stats.tsx`: a solid bar of icon + count + label
 * columns separated by thin vertical rules.
 *
 * BREAKPOINTS — the v1 source uses Tailwind's default scale where `md:` is 768px.
 * This app remaps `md:` to 1230px, so every `md:` from the reference is `tab:` here.
 *
 * BORDERS — v1 is Tailwind v3, whose preflight defaults `border-color` to #e5e7eb.
 * This app is Tailwind v4, where the default is `currentColor` (white here, which
 * would make the icon ring vanish), so the ring colour is set explicitly.
 */

interface MetaItem {
  id?: string | number;
  meta_icon?: string;
  meta_icon_alt?: string;
  /** The number itself, e.g. "3600+" */
  meta_title?: string;
  /** The label under it, e.g. "Students" */
  meta_subtitle?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers1026Data {
  metadata?: MetaItem[];
  /** Bar background */
  background_color?: string;
  /** Count text colour */
  title_start_color?: string;
  /** Label text colour */
  description_color?: string;
  /** Vertical rule between columns */
  gradient_color_1?: string;
  /** Icon circle fill */
  gradient_color_2?: string;
  /** Icon circle ring */
  gradient_color_3?: string;
  status?: boolean;
}

const KeyNumbers1026 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as KeyNumbers1026Data | undefined;

  const {
    background_color = "#836537",
    title_start_color = "#FFFFFF",
    description_color = "#FFFFFF",
    gradient_color_1 = "#22202369",
    gradient_color_2 = "#FFFFFF",
    gradient_color_3 = "#e5e7eb",
  } = sectionData || {};

  const stats = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || stats.length === 0) return null;

  return (
    <div
      className="mx-auto flex max-h-[160px] w-full items-center justify-center text-white"
      style={{ backgroundColor: background_color }}
    >
      <section className="relative mx-auto w-full !overflow-hidden p-0">
        <div className="flex justify-between px-[21px] py-[18px] tab:px-[200px]">
          {stats.map((stat, index) => (
            <React.Fragment key={stat.id ?? index}>
              <div className="flex h-full flex-1 flex-col items-center">
                {/* Desktop icon — 25x30 */}
                <div
                  className="hidden size-[50px] rounded-[50px] border tab:flex tab:items-center tab:justify-center"
                  style={{
                    backgroundColor: gradient_color_2,
                    borderColor: gradient_color_3,
                  }}
                >
                  {stat.meta_icon && (
                    <Image
                      src={stat.meta_icon}
                      alt={stat.meta_icon_alt || "Icon"}
                      width={25}
                      height={30}
                      loading="lazy"
                    />
                  )}
                </div>
                {/* Mobile icon — 25x25 */}
                <div
                  className="flex size-[50px] items-center justify-center rounded-[50px] border tab:hidden"
                  style={{
                    backgroundColor: gradient_color_2,
                    borderColor: gradient_color_3,
                  }}
                >
                  {stat.meta_icon && (
                    <Image
                      src={stat.meta_icon}
                      alt={stat.meta_icon_alt || "Icon"}
                      width={25}
                      height={25}
                      loading="lazy"
                    />
                  )}
                </div>
                <p
                  className="mt-2 w-fit text-2xl font-semibold"
                  style={{ color: title_start_color }}
                >
                  {stat.meta_title}
                </p>
                <p
                  className="w-fit text-center text-sm font-extralight"
                  style={{ color: description_color }}
                >
                  {stat.meta_subtitle}
                </p>
              </div>

              {index !== stats.length - 1 && (
                <div
                  className={cn("mx-4 h-24 w-px")}
                  style={{ backgroundColor: gradient_color_1 }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>
    </div>
  );
};

export default KeyNumbers1026;
